import { DpContainer, DpContainerProps } from '@/components/blocks/DpContainer';
import { Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { combineSX } from '@/utils/combineSX';
import { Grid, SxProps, Typography, TypographyProps } from '@mui/material';

export const DpRichContentFrame: React.FC<React.PropsWithChildren<DpContainerProps>> = ({
	children,
	framesx,
	...props
}) => (
	<DpContainer
		withoutmargin
		framesx={combineSX([
			{
				backgroundColor: '#F8F8F8',
				paddingTop: { xs: '24px', md: '40px' },
				paddingBottom: { xs: '24px', md: '60px' },
			},
			...(Array.isArray(framesx) ? framesx : [framesx]),
		])}
		{...props}
	>
		{children}
	</DpContainer>
);

export const DpRichContentTitle: React.FC<React.PropsWithChildren<TypographyProps>> = ({
	children,
	sx,
	...props
}) => (
	<Typography
		component="h2"
		sx={combineSX([
			{
				fontSize: '24px',
				fontWeight: '800',
				color: '#2C2C2C',
				lineHeight: { xs: '22x', md: '40px' },
				marginBottom: { xs: '4px', md: '6px' },
			},
			...(Array.isArray(sx) ? sx : [sx]),
		])}
		{...props}
	>
		{children}
	</Typography>
);

export const DpRichContentDescription: React.FC<React.PropsWithChildren<TypographyProps>> = ({
	children,
	sx,
	...props
}) => (
	<Typography
		sx={combineSX([
			{
				fontSize: '15px',
				fontWeight: '500',
				color: '#2C2C2C',
				lineHeight: '20px',
			},
			...(Array.isArray(sx) ? sx : [sx]),
		])}
		{...props}
	>
		{children}
	</Typography>
);

export const DpRichContentBanner: React.FC<{
	image: { desktop: string; mobile: string };
	alt: string;
	href?: string;
	sx?: SxProps;
}> = ({ image, alt, href, sx }) => {
	const isMobile = useIsMobile();

	return (
		<Linkable href={href}>
			<Img
				src={isMobile ? image.mobile : image.desktop}
				alt={alt}
				sx={combineSX([
					{
						marginTop: { xs: '20px', md: '30px' },
						marginBottom: { xs: '20px', md: '20px' },
					},
					...(Array.isArray(sx) ? sx : [sx]),
				])}
			/>
		</Linkable>
	);
};

export const DpRichContentGridAds: React.FC<
	React.PropsWithChildren<{
		framesx?: SxProps;
		innersx?: SxProps;
	}>
> = ({ children, framesx, innersx }) => (
	<Grid
		container
		xs={12}
		marginTop={{ xs: '20px', md: '30px' }}
		sx={combineSX([
			{
				marginTop: { xs: '20px', md: '30px' },
			},
			...(Array.isArray(framesx) ? framesx : [framesx]),
		])}
	>
		<Grid
			item
			container
			columnSpacing={{ xs: 0, md: '20px' }}
			rowSpacing={{ xs: '10px', md: 0 }}
			sx={innersx}
		>
			{children}
		</Grid>
	</Grid>
);

export const DpRichContentGridAd: React.FC<{
	src: string;
	href?: string;
	alt: string;
	framesx?: SxProps;
	imgsx?: SxProps;
}> = ({ src, href, alt, framesx, imgsx }) => (
	<Grid item xs={12} md={6} sx={framesx}>
		<Linkable href={href}>
			<Img src={src} alt={alt} sx={imgsx} />
		</Linkable>
	</Grid>
);
